import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const PrivacyPolicy = () => {
  const { language, setLanguage, content } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Header
        language={language}
        onLanguageChange={setLanguage}
        content={content.header}
      />
      
      <main className="pt-20">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8 hero-text">Integritetspolicy</h1>
            
            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold mb-4">Personuppgiftsansvarig</h2>
                <div className="p-4 bg-muted rounded-lg">
                  <p><strong>Volym Digitalbyrå AB</strong></p>
                  <p>Organisationsnummer: [Org.nr]</p>
                  <p>E-post: info@volymkommunikation.se</p>
                  <p>Telefon: +46 10 750 28 66</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Vilka personuppgifter samlar vi in?</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Vi samlar in följande personuppgifter när du kontaktar oss genom vårt kontaktformulär eller på annat sätt:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Namn</li>
                  <li>E-postadress</li>
                  <li>Telefonnummer (om du väljer att ange det)</li>
                  <li>Företagsnamn (om du väljer att ange det)</li>
                  <li>Meddelande/förfrågan</li>
                  <li>IP-adress och webbläsarinformation (automatiskt)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Varför behandlar vi dina personuppgifter?</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Vi behandlar dina personuppgifter för följande ändamål:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>För att kunna svara på dina förfrågningar och frågor</li>
                  <li>För att tillhandahålla våra tjänster och produkter</li>
                  <li>För kundkommunikation och support</li>
                  <li>För att förbättra vår webbplats och våra tjänster</li>
                  <li>För att uppfylla våra rättsliga förpliktelser</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  <strong>Rättslig grund:</strong> Vår behandling av dina personuppgifter grundar sig på ditt samtycke och vårt berättigade intresse att bedriva verksamhet och tillhandahålla våra tjänster.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Hur länge sparar vi dina uppgifter?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Vi sparar dina personuppgifter endast så länge det är nödvändigt för de ändamål för vilka de samlades in:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-4">
                  <li>Kontaktförfrågningar: 2 år från senaste kontakt</li>
                  <li>Kundrelationer: Under avtalsperioden och 3 år därefter</li>
                  <li>Webbplatsanalys: 26 månader (Google Analytics)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Dina rättigheter</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Enligt GDPR har du följande rättigheter:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li><strong>Rätt till information:</strong> Du har rätt att få information om hur vi behandlar dina personuppgifter</li>
                  <li><strong>Rätt till tillgång:</strong> Du har rätt att få en kopia av de personuppgifter vi har om dig</li>
                  <li><strong>Rätt till rättelse:</strong> Du har rätt att få felaktiga uppgifter rättade</li>
                  <li><strong>Rätt till radering:</strong> Du har rätt att få dina uppgifter raderade under vissa omständigheter</li>
                  <li><strong>Rätt till begränsning:</strong> Du har rätt att begära att behandlingen begränsas</li>
                  <li><strong>Rätt till dataportabilitet:</strong> Du har rätt att få ut dina uppgifter i ett strukturerat format</li>
                  <li><strong>Rätt att göra invändningar:</strong> Du har rätt att invända mot behandlingen</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Delning av personuppgifter</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Vi delar inte dina personuppgifter med tredje part utan ditt samtycke, förutom:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>När det krävs enligt lag</li>
                  <li>Till våra leverantörer som hjälper oss att driva vår verksamhet (under databehandlaravtal)</li>
                  <li>Google Analytics för webbplatsanalys (anonymiserat)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Säkerhet</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda dina personuppgifter mot obehörig åtkomst, förlust eller förstörelse. Detta inkluderar kryptering, säkra servrar och begränsad åtkomst till personuppgifter.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Klagomål</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Om du har klagomål på hur vi behandlar dina personuppgifter har du rätt att lämna in ett klagomål till Integritetsskyddsmyndigheten (IMY).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">Kontakta oss</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Om du har frågor om denna integritetspolicy eller vill utöva dina rättigheter, kontakta oss på:
                </p>
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p><strong>Volym Digitalbyrå AB</strong></p>
                  <p>E-post: info@volymkommunikation.se</p>
                  <p>Telefon: +46 10 750 28 66</p>
                </div>
              </section>

              <section>
                <p className="text-sm text-muted-foreground">
                  Senast uppdaterad: {new Date().toLocaleDateString('sv-SE')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer content={content.footer} />
    </div>
  );
};

export default PrivacyPolicy;